import React, { useState } from "react";
import { Modal, Input, Row, Col, Button, Select, Tag } from "antd";
import { CloseOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import DeleteIcon from "../../assets/images/delete1.svg";
import BarCodeHandler from "../BarcodeHandler";
import IntegratedWeightModal from "./IntegratedWeightModal";
import { Scrollbars } from "react-custom-scrollbars";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useAdvanceOrderStore from "../../stores/useAdvanceOrderStore";
import { getUomIcon, needsPopup } from "../hooks/productPopupUtils";
import useProductPopupSearch from "../hooks/useProductPopupSearch";
import useProductWeight from "../hooks/useProductWeight";
import useProductQuantity from "../hooks/useProductQuantity";

const ProductsPopup = (props) => {
  const {
    productData,
    visibleProductsModal,
    setVisibleProductsModal,
    selectedItems,
    setSelectedItems,
    onSubmit,
    selectedProduct,
    setSelectedProduct,
    selectedProductCallback,
    weightPopup,
    setWeightPopup,
    selectedProductInCart,
    setSelectedProductInCart,
    cart,
    setCart,
    setProductStock,
  } = props;

  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
  const isPreOrderPage = window.location.pathname.includes("/preorder");
  const [showProductSelection, setShowProductSelection] = useState(false);

  // --- Compose hooks ---

  const search = useProductPopupSearch({
    productData,
    selectedItems,
    visibleProductsModal,
  });

  const weight = useProductWeight({
    selectedProduct,
    setSelectedProduct,
    selectedProductCallback,
    weightPopup,
    setWeightPopup,
    selectedProductInCart,
    setSelectedProductInCart,
  });

  const quantity = useProductQuantity({
    selectedItems,
    setSelectedItems,
    cart,
    setCart,
    setProductStock,
    unit: weight.unit,
  });

  // --- Modal-level actions ---

  const resetModalState = () => {
    setSelectedItems([]);
    search.resetSearch();
    setShowProductSelection(false);
    setSelectedProduct({});
    weight.resetWeight();
  };

  const handleUomCancel = () => {
    weight.handleUomCancel();
    if (!showProductSelection) {
      resetModalState();
      // Resolve the awaited add-promise on Cancel / mask-close so
      // addDefinedProduct's in-flight guard is released (mirrors the weight
      // popup's onCancel). Without this the alternate-UOM popup leaves the
      // promise unsettled, permanently locking the guard so every later
      // product selection is silently dropped until a page refresh.
      selectedProductCallback?.(null);
    }
  };

  const handleUomConfirmWrapper = () => {
    if (showProductSelection) {
      weight.handleUomConfirm(async (updatedProduct) => {
        const newValue = updatedProduct?.rawWeight || updatedProduct?.weight?.toString() || "1";
        await quantity.handleQuantityChange(updatedProduct, newValue);
      });
    } else {
      weight.handleUomConfirm();
    }
  };

  const openLocalPopup = (product) => {
    const existing = selectedItems.find((i) => i.mProductId === product.mProductId && i.uom === (product.uom || product.csUomId));
    const productWithWeight = {
      ...product,
      weight: existing?.weight || product.weight || 0,
      rawWeight: existing?.rawWeight || product.rawWeight || product.weight?.toString() || "",
    };
    setSelectedProduct(productWithWeight);
    const hasAltUom = product?.alternateUom?.length > 0 && product?.showAlternateUomInPos === "Y";
    if (!hasAltUom) {
      setWeightPopup(true);
    }
  };

  const handleSubmit = () => {
    const updatedItems = selectedItems.map((item) => ({
      ...item,
      weight: Number(item.weight),
      rawWeight: item.rawWeight || item.weight.toString(),
      unit: item.unit || weight.unit || item?.uomData?.[0]?.name?.toLowerCase() || "kg",
    }));
    const isStrictQtyLocal = posConfig?.requireQtyBeforeAddingItem === "Y";
    if (isStrictQtyLocal && updatedItems.some((item) => Number(item.weight) <= 0)) return;
    onSubmit(updatedItems);
    setVisibleProductsModal(false);
    resetModalState();
  };

  // --- Render ---
  // GPR: gate add-to-cart on quantity entry when posConfig.requireQtyBeforeAddingItem === "Y"
  const isStrictQty = posConfig?.requireQtyBeforeAddingItem === "Y";
  const isZeroWeight = Number(selectedProduct?.weight || 0) <= 0;

  return (
    <>
      {/* UOM Selection Modal */}
      <Modal
        open={weight.uomPopup}
        zIndex={2000}
        closable={false}
        onCancel={handleUomCancel}
        keyboard={false}
        footer={null}
        width="400px"
        centered
        styles={{ body: { padding: "24px", backgroundColor: "#fff" } }}
        title={null}
        afterOpenChange={(open) => {
          if (open && weight.uomInputRef?.current) {
            setTimeout(() => {
              weight.uomInputRef.current?.focus();
              weight.uomInputRef.current?.select?.();
            }, 50);
          }
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Header */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#000" }}>Select Unit for {weight.pendingProduct?.name}</h3>
            </div>

            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#666" }}>Choose whether to sell by pieces or weight</p>
          </div>

          {/* UOM Selection Cards */}
          <div style={{ display: "flex", gap: "12px" }}>
            {weight.availableUoms.map((uom) => (
              <div
                key={uom.csUomId}
                onClick={() => {
                  if (weight.isUomLocked) return;
                  weight.setSelectedUomId(uom.csUomId);
                  const isKg = uom?.decimal === "Y";
                  if (!isKg) {
                    weight.setUomQuantity("1");
                  } else {
                    weight.setUomQuantity("0");
                  }
                  requestAnimationFrame(() => {
                    weight.uomInputRef.current?.focus?.();
                    weight.uomInputRef.current?.select?.();
                  });
                }}
                style={{
                  flex: 1,
                  padding: "16px",
                  border: weight.selectedUomId === uom.csUomId ? "2px solid #000" : "1px solid #e0e0e0",
                  borderRadius: "8px",
                  cursor: weight.isUomLocked && uom?.decimal !== "Y" ? "not-allowed" : "pointer",
                  backgroundColor: weight.selectedUomId === uom.csUomId ? "#000" : "#fff",
                  color: weight.selectedUomId === uom.csUomId ? "#fff" : "#000",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                {getUomIcon(uom.name)}
                <span style={{ fontSize: "14px", fontWeight: 500 }}>{uom.name}</span>
              </div>
            ))}
          </div>

          {/* Quantity Input */}
          <div>
            <label
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#000",
              }}
            >
              <span>Quantity ({weight.selectedUom?.ediCode || weight.selectedUom?.name || "units"})</span>

              {/* {posConfig?.showStoreSpecificStock === "Y" && <span>Stock: {selectedProduct?.onhandQty ?? 0}</span>} */}
              {posConfig?.showStoreSpecificStock === "Y" && <span>Stock: {selectedProduct?.stock ?? 0}</span>}
            </label>
            <Input
              ref={weight.uomInputRef}
              type="text"
              value={weight.uomQuantity}
              onChange={(e) => weight.handleUomInputChange(e.target.value)}
              disabled={!isAdvanceOrderEnabled && !isPreOrderPage && selectedProduct?.restrictPosQtyEdit === "Y" && weight.selectedUom?.name?.toLowerCase() === "kilogram"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (isStrictQty && Number(weight.uomQuantity) <= 0) return;
                  handleUomConfirmWrapper();
                }
                const isDecimal = weight.selectedUom?.decimal === "Y";
                if (!isDecimal && e.key === ".") {
                  e.preventDefault();
                }
              }}
              style={{
                width: "100%",
                height: "48px",
                fontSize: "16px",
                borderRadius: "8px",
                fontWeight: 500,
                border: "1px solid #e0e0e0",
                color: "#000"
              }}
            />
          </div>

          {/* Total Price */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "14px", color: "#666" }}>Total Price</span>
            <span style={{ fontSize: "24px", fontWeight: 600, color: "#000" }}>₹{Number(weight.uomTotalPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
            <Button
              onClick={handleUomCancel}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                color: "#000",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUomConfirmWrapper}
              disabled={isStrictQty && Number(weight.uomQuantity) <= 0}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: isStrictQty && Number(weight.uomQuantity) <= 0 ? "#ccc" : "#000",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isStrictQty && Number(weight.uomQuantity) <= 0 ? "not-allowed" : "pointer",
              }}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </Modal>

      {/* Products Selection Modal */}
      <Modal
        open={visibleProductsModal}
        centered={true}
        width={"72%"}
        closable={true}
        closeIcon={
          <CloseOutlined
            style={{ color: "#2F3856", fontSize: "16px" }}
            onClick={() => {
              setVisibleProductsModal(false);
              localStorage.removeItem("productSegment");
              resetModalState();
            }}
          />
        }
        footer={null}
      >
        <BarCodeHandler setSelectedItems={setSelectedItems} />
        {showProductSelection ? (
          <div style={{ padding: "5vh 0 0 0", height: "85vh", position: "relative" }}>
            <Input
              placeholder="Search by Name / SKU / UPC / Description / Batch"
              value={search.rawSearchQuery}
              allowClear
              onChange={(e) => {
                search.setRawSearchQuery(e.target.value);
                search.debouncedSetSearch(e.target.value);
              }}
              style={{
                borderRadius: "7px",
                padding: "0.8vh 0.8vw",
                border: "1px solid #d9d9d9",
                width: "100%",
              }}
            />

            {/* Filter bar: Category pills + Brand dropdown + Sort + Clear All */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "1vh 0", flexWrap: "wrap" }}>
              {/* Category pills */}
              <div style={{ display: "flex", gap: "4px", overflowX: "auto", flex: 1, minWidth: 0 }}>
                <Tag
                  color={!search.selectedCategory ? "#2F3856" : undefined}
                  style={{ cursor: "pointer", borderRadius: "12px", padding: "2px 10px", whiteSpace: "nowrap" }}
                  onClick={() => search.setSelectedCategory(null)}
                >
                  All
                </Tag>
                {search.categoriesData.map((cat) => (
                  <Tag
                    key={cat.value}
                    color={search.selectedCategory === cat.value ? "#2F3856" : undefined}
                    style={{ cursor: "pointer", borderRadius: "12px", padding: "2px 10px", whiteSpace: "nowrap" }}
                    onClick={() => search.setSelectedCategory(search.selectedCategory === cat.value ? null : cat.value)}
                  >
                    {cat.label}
                  </Tag>
                ))}
              </div>

              {/* Brand dropdown */}
              {search.brandsData.length > 0 && (
                <Select
                  placeholder="Brand"
                  allowClear
                  value={search.selectedBrand}
                  onChange={(val) => search.setSelectedBrand(val || null)}
                  style={{ width: "140px", flexShrink: 0 }}
                  size="small"
                >
                  {search.brandsData.map((brand) => (
                    <Select.Option key={brand.value} value={brand.value}>
                      {brand.label}
                    </Select.Option>
                  ))}
                </Select>
              )}

              {/* Sort dropdown */}
              <Select value={search.sortOrder} onChange={(val) => search.setSortOrder(val)} style={{ width: "150px", flexShrink: 0 }} size="small">
                <Select.Option value="default">Default</Select.Option>
                <Select.Option value="price_asc">Price Low→High</Select.Option>
                <Select.Option value="price_desc">Price High→Low</Select.Option>
                <Select.Option value="name_asc">Name A→Z</Select.Option>
                <Select.Option value="name_desc">Name Z→A</Select.Option>
              </Select>

              {/* Clear All */}
              {(search.selectedCategory || search.selectedBrand || search.sortOrder !== "default") && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    search.resetSearch();
                  }}
                  style={{ flexShrink: 0, padding: 0, fontSize: "12px" }}
                >
                  Clear All ({[search.selectedCategory, search.selectedBrand, search.sortOrder !== "default"].filter(Boolean).length})
                </Button>
              )}
            </div>

            <Row style={{ padding: "1vh 0" }} gutter={[16, 16]}>
              {search.paginatedData.length > 0 ? (
                search.paginatedData.map((item, index) => {
                  const matchingItems = selectedItems.filter((ci) => ci.mProductId === item.mProductId);
                  const cartItem = matchingItems.length > 0 ? matchingItems[matchingItems.length - 1] : null;
                  const uomName = cartItem?.uomData?.[0]?.name ?? item?.uomData?.[0]?.name ?? "";
                  const qty = cartItem ? cartItem.rawWeight || cartItem.weight.toString() : "0";
                  const isDecimal = (cartItem || item)?.uomData?.[0]?.decimal === "Y";

                  return (
                    <Col key={index} span={12} style={{ padding: "0 8px" }}>
                      <Row style={{ padding: "1.5%", width: "100%", border: "1px solid #d9d9d9", height: "10vh" }}>
                        <Col span={12}>
                          <p
                            title={item.name}
                            style={{
                              margin: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {item.name}
                          </p>
                          <p style={{ margin: 0 }}>
                            {item.sunitprice} - {uomName} {posConfig?.showStoreSpecificStock === "Y" && item.onhandQty !== undefined ? `- ${item.onhandQty ?? 0}` : ""}
                          </p>
                        </Col>
                        <Col span={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Button
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openLocalPopup(item);
                                } else {
                                  quantity.handleDecrease(item);
                                }
                              }}
                              style={{ backgroundColor: "#2F3856", color: "#fff", width: "2.5vw", height: "4vh" }}
                              icon={<MinusOutlined />}
                            />
                            <Input
                              type="text"
                              value={qty}
                              readOnly={needsPopup(item)}
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openLocalPopup(item);
                                }
                              }}
                              onChange={(e) => {
                                if (!needsPopup(item)) {
                                  quantity.handleQuantityChange(item, e.target.value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const newValue = e.target.value || "0";
                                  quantity.handleQuantityChange(item, newValue);
                                } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                const newValue = e.target.value || "0";
                                if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                                  quantity.handleQuantityChange(item, "0");
                                } else {
                                  quantity.handleQuantityChange(item, newValue);
                                }
                              }}
                              placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                              style={{ width: "60px", height: "4vh", textAlign: "center" }}
                            />
                            <Button
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openLocalPopup(item);
                                } else {
                                  quantity.handleIncrease(item);
                                }
                              }}
                              style={{ backgroundColor: "#2F3856", color: "#fff", width: "2.5vw", height: "4vh" }}
                              icon={<PlusOutlined />}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  );
                })
              ) : (
                <p style={{ textAlign: "center", width: "100%" }}>No products found</p>
              )}
            </Row>

            <div style={{ position: "absolute", bottom: "0vh", width: "100%", padding: "1vh 0" }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Button
                    disabled={search.currentPage === 0}
                    onClick={search.handlePreviousPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: search.currentPage === 0 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                  >
                    {"<"}
                  </Button>
                  <span style={{ fontWeight: "bold", margin: "0 1vw" }}>
                    Page {search.currentPage + 1} of {search.totalPages || 1}
                  </span>
                  <Button
                    disabled={search.currentPage >= search.totalPages - 1}
                    onClick={search.handleNextPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: search.currentPage >= search.totalPages - 1 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginLeft: "1vw",
                    }}
                  >
                    {">"}
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => setShowProductSelection(false)}
                    style={{
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      width: "8vw",
                      height: "6.5vh",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                    disabled={selectedItems.length === 0}
                  >
                    Okay
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ height: "81vh", position: "relative" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: "2vh", marginTop: "3vh" }}>
              <Col>
                <h3>{selectedProduct?.name || ""}</h3>
              </Col>
              <Col>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowProductSelection(true)} style={{ backgroundColor: "#2F3856", borderColor: "#2F3856" }}>
                  Add Products
                </Button>
              </Col>
            </Row>
            <Row
              style={{
                padding: "0.1rem 0.75rem",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#2F3856",
                borderBottom: "1px solid #e8e8e8",
                marginBottom: "0.5rem",
              }}
            >
              <Col span={6}>Name</Col>
              <Col span={4} style={{ textAlign: "right" }}>
                Unit Price
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Qty
              </Col>
              <Col span={2} style={{ textAlign: "right" }}>
                Total Qty
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Total Price
              </Col>
              <Col span={2}></Col>
            </Row>
            <Scrollbars style={{ height: "60vh" }}>
              {selectedItems.map((item) => {
                const unitPrice = parseFloat(item.sunitprice) || 0;
                const itemQty = parseFloat(item.weight) || 0;
                const totalPrice = unitPrice * itemQty;
                const isDecimal = item?.uomData?.[0]?.decimal === "Y";
                return (
                  <Row
                    key={item.id || item.name}
                    style={{
                      padding: "0.5rem 0.9rem",
                      width: "100%",
                      marginBottom: "0.4rem",
                      height: "auto",
                      alignItems: "center",
                    }}
                  >
                    <Col span={6}>
                      <p
                        title={item.name}
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#333",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {item.name}
                      </p>
                    </Col>
                    <Col span={4} style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{unitPrice.toLocaleString("en-IN")}</p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <Input
                        type="text"
                        value={item.weight || item.rawWeight || ""}
                        onChange={(e) => quantity.handleQuantityChange(item, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            quantity.handleQuantityChange(item, e.target.value || "0");
                          } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value || "0";
                          if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                            quantity.handleQuantityChange(item, "0");
                          } else {
                            quantity.handleQuantityChange(item, newValue);
                          }
                        }}
                        placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                        style={{ width: "60px", height: "25px", textAlign: "center", fontSize: "0.8rem" }}
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{itemQty}</p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{totalPrice.toLocaleString("en-IN")}</p>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      <img src={DeleteIcon} style={{ height: "2.5vh", cursor: "pointer" }} onClick={() => quantity.handleDelete(item)} />
                    </Col>
                  </Row>
                );
              })}
            </Scrollbars>
            <Button
              style={{
                position: "absolute",
                bottom: "0.5rem",
                right: "0.5rem",
                backgroundColor: "#2F3856",
                color: "#fff",
                width: "7vw",
                height: "4.5vh",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                fontSize: "0.85rem",
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        )}
      </Modal>

      {/* Weight Popup — uses shared IntegratedWeightModal */}
      <IntegratedWeightModal
        open={weightPopup && !(selectedProduct?.alternateUom?.length > 0 && selectedProduct?.showAlternateUomInPos === "Y")}
        product={selectedProduct}
        onTotalQty={weight.handleTotalQty}
        onInputChange={(v) => weight.handleInputChange(v.replace(/[^0-9.]/g, ""))}
        onConfirm={() => {
          if (isStrictQty && Number(selectedProduct?.weight || 0) <= 0) return;
          const updatedProduct = {
            ...selectedProduct,
            weight: isStrictQty ? selectedProduct.weight : (Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight),
            rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
            unit: weight.unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
          };
          setSelectedProduct(updatedProduct);
          setWeightPopup(false);
          if (showProductSelection) {
            const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString() || "1";
            quantity.handleQuantityChange(updatedProduct, newValue);
          } else {
            selectedProductCallback(updatedProduct);
          }
        }}
        onCancel={() => {
          setWeightPopup(false);
          if (!showProductSelection) {
            selectedProductCallback(null);
          }
        }}
        inputRef={weight.weightInputRef}
        onRefresh={weight.callGetWeightAPI}
        unit={weight.unit}
        onUnitChange={weight.setUnit}
        frequentWeights={weight.frequentWeights}
        onFrequentWeight={weight.handleFrequentWeight}
        strictQty={isStrictQty}
      />
    </>
  );
};

export default ProductsPopup;
